import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const LIKERT_OPTIONS = [
  "Strongly Disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly Agree"
];

export default function TakeAssessmentModal({ isOpen, onClose, assessment, mode = 'take', userResult = {} }) {
  const [answers, setAnswers] = useState(userResult || {});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    setAnswers(userResult || {});
    setShowSuccess(false);
  }, [userResult]);

  if (!isOpen || !assessment) return null;

  const questions = assessment.questions || [];
  const isView = mode === 'view';
  const isPreview = mode === 'preview';

  const handleChange = (qid, value) => {
    setAnswers({ ...answers, [qid]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await assessment.onSubmit(answers);
      setShowSuccess(true);
      // Auto-close after 3 seconds or let user click close
      setTimeout(() => {
        if (showSuccess) {
          onClose();
        }
      }, 3000);
    } catch (err) {
      setError('Failed to submit assessment.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  // Success screen after submission
  if (showSuccess) {
    const successContent = (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-green-200 max-w-md w-full mx-4 text-center p-8">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-800 mb-4">Assessment Submitted!</h2>
          <p className="text-gray-700 mb-6">
            Thank you for completing the assessment. Your responses have been recorded successfully.
          </p>
          <button 
            onClick={handleClose}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
    return createPortal(successContent, document.body);
  }

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-2xl shadow-2xl border-2 border-blue-200 max-w-3xl w-full mx-4 mt-40 flex flex-col"
        style={{ maxHeight: '80vh' }}
      >
        {/* Modal Title */}
        <div className="w-full bg-blue-600 rounded-t-2xl px-8 py-5 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white m-0">{assessment.title || assessment.assessment_type || 'Assessment (No Title)'}</h2>
          <button
            onClick={handleClose}
            className="ml-4 text-blue-100 hover:text-white text-2xl font-bold"
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>
        {/* Description */}
        <div className="px-8 pt-6 pb-2 bg-white">
          {(assessment.template && assessment.template.description) || assessment.description ? (
            <p className="text-gray-700 text-base">{(assessment.template && assessment.template.description) || assessment.description}</p>
          ) : (
            <p className="text-gray-400 text-base">No description provided.</p>
          )}
        </div>
        {/* Divider below description */}
        <div className="w-full border-t border-gray-200" />
        {/* Modal Content */}
        <div className="p-8 overflow-y-auto bg-gray-50 rounded-b-2xl" style={{ flex: 1 }}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {questions.length === 0 ? (
              <div>No questions found.</div>
            ) : (
              questions.map(q => (
                <div key={q.id} className="mb-6">
                  <label className="block font-bold text-lg mb-2 text-gray-800">{q.text}</label>
                  {isView ? (
                    <div className="bg-gray-100 rounded px-3 py-2 min-h-[2.5em] flex items-center">
                      {/* Since backend now returns formatted results, we can display them directly */}
                      {Object.entries(userResult).map(([questionText, answer]) => {
                        if (questionText === q.text || questionText.includes(q.text)) {
                          return <span key={questionText}>{answer}</span>;
                        }
                        return null;
                      })}
                      {/* Fallback for direct question ID match */}
                      {answers[q.id] !== undefined && answers[q.id] !== '' ? (
                        <span>{answers[q.id]}</span>
                      ) : (
                        <span className="text-gray-400">No answer</span>
                      )}
                    </div>
                  ) : q.type === 'likert' ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {LIKERT_OPTIONS.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'comfort_scale' && q.options && Array.isArray(q.options) ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {q.options.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'confidence_scale' && q.options && Array.isArray(q.options) ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {q.options.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'frequency_scale' && q.options && Array.isArray(q.options) ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {q.options.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'scale' ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {[1,2,3,4,5].map(n => (
                        <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={n}
                            checked={answers[q.id] === n}
                            onChange={() => handleChange(q.id, n)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{n}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'multi_select' && q.options && Array.isArray(q.options) ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {q.options.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={answers[q.id]?.includes(opt)}
                            onChange={(e) => {
                              const currentAnswers = answers[q.id] || [];
                              const newAnswers = e.target.checked
                                ? [...currentAnswers, opt]
                                : currentAnswers.filter(a => a !== opt);
                              handleChange(q.id, newAnswers);
                            }}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'single_select' && q.options && Array.isArray(q.options) ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      {q.options.map((opt, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}
                            disabled={isSubmitting || isPreview}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'yes_no' ? (
                    <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          value="Yes"
                          checked={answers[q.id] === "Yes"}
                          onChange={() => handleChange(q.id, "Yes")}
                          disabled={isSubmitting || isPreview}
                        />
                        <span>Yes</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          value="No"
                          checked={answers[q.id] === "No"}
                          onChange={() => handleChange(q.id, "No")}
                          disabled={isSubmitting || isPreview}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  ) : q.type === 'ranking' && q.options && Array.isArray(q.options) ? (
                    <div>
                      <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                        Drag items to rank them (1 = most important):
                      </div>
                      <div>
                        {(answers[q.id] || q.options).map((item, index) => (
                          <div
                            key={item}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '8px 12px',
                              margin: '4px 0',
                              background: isPreview || isSubmitting ? '#f5f5f5' : '#fff',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              cursor: isPreview || isSubmitting ? 'default' : 'move'
                            }}
                            draggable={!isPreview && !isSubmitting}
                            onDragStart={(e) => {
                              if (isPreview || isSubmitting) return;
                              e.dataTransfer.setData('text/plain', index);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              if (isPreview || isSubmitting) return;
                              e.preventDefault();
                              const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                              const toIndex = index;
                              if (fromIndex !== toIndex) {
                                const currentOrder = answers[q.id] || [...q.options];
                                const newOrder = [...currentOrder];
                                const [moved] = newOrder.splice(fromIndex, 1);
                                newOrder.splice(toIndex, 0, moved);
                                handleChange(q.id, newOrder);
                              }
                            }}
                          >
                            <span style={{ fontSize: '1.2rem', color: '#666' }}>☰</span>
                            <span style={{ fontWeight: 'bold', minWidth: '20px', color: '#333' }}>{index + 1}.</span>
                            <span style={{ flex: 1 }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : q.type === 'long_text' ? (
                    <textarea
                      className="w-full border rounded px-3 py-2 min-h-[3em] bg-white"
                      value={answers[q.id] || ''}
                      onChange={e => handleChange(q.id, e.target.value)}
                      disabled={isSubmitting || isPreview}
                      placeholder={q.placeholder || ''}
                    />
                  ) : (
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2 bg-white"
                      value={answers[q.id] || ''}
                      onChange={e => handleChange(q.id, e.target.value)}
                      disabled={isSubmitting || isPreview}
                      placeholder={q.placeholder || ''}
                    />
                  )}
                  {q.helper_text && <div className="text-gray-500 text-xs mt-1">{q.helper_text}</div>}
                </div>
              ))
            )}
            {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
            <div className="flex justify-end gap-4 mt-8">
              <button type="button" onClick={handleClose} className="bg-gray-200 text-gray-700 px-6 py-2 rounded font-semibold hover:bg-gray-300 transition-colors" disabled={isSubmitting}>
                {isView ? 'Close' : 'Cancel'}
              </button>
              {!isView && !isPreview && (
                <button type="submit" className="bg-blue-700 text-white px-6 py-2 rounded font-semibold hover:bg-blue-800 transition-colors" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
} 